import {i18n} from '@yourcoach/i18n';

import type {CourseApiModel, CourseExpandedCommon} from '../../../api/course';
import type {ApiCountResponse} from '../../../api/types';
import type {
  CoreCoachCoursesCountParams,
  CoreCoachCoursesStartParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {courseExpandCommon, courseStore} from '../../../api/course';
import {rewardProfileStore} from '../../../api/gamification';
import {currentUserStore} from '../../../api/user';
import {apiRequest} from '../../../api/utils/apiRequest';
import {alert} from '../../../components/alert';
import {REQUEST_REVIEW_AFTER_COURSE_START_EVENT} from '../../../core/system/appStoreReview/events';
import {not} from '../../../utils/conditionalUtils';
import {isCourseIndividual} from '../../../utils/entity/course';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {AppError} from '../../../utils/errors/app';
import {emitter} from '../../../utils/eventEmitter';
import {logger} from '../../../utils/logger';
import {platform} from '../../../utils/platform';
import {getSubscriptionLimits} from '../../subscription/getSubscriptionLimits';
import {COMMAND} from '../utils';

export {courseStart};

type Config = BaseConfig<Omit<CoreCoachCoursesStartParams, '_id'>> & {
  entity: CourseApiModel;
};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  if (isMeCoachForEntity(config.entity)) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function courseStart(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

courseStart.asClient = asClient;
courseStart.asCoach = asCoach;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({entity, params}: Config) {
  try {
    const shouldLimitOngoingCourses = rewardProfileStore.rewardProfile
      ? rewardProfileStore.rewardProfile.payment_status === 'trial'
      : false;

    if (shouldLimitOngoingCourses) {
      const {isGroupLimitReached, isIndividualLimitReached} =
        await getSubscriptionLimits();

      const isTargetCourseIndividual = isCourseIndividual(entity);

      const isIndividualLimitWillBeReached =
        isTargetCourseIndividual && isIndividualLimitReached;

      const isGroupLimitWillBeReached =
        not(isTargetCourseIndividual) && isGroupLimitReached;

      const shouldPreventStartByLimit =
        isIndividualLimitWillBeReached || isGroupLimitWillBeReached;

      if (shouldPreventStartByLimit) {
        throw {
          buttons: [
            {
              label: i18n.t('shared:ok'),
              onClick: () => {
                rewardProfileStore.sendSubscriptionInfo();
              },
            },
          ],
          message: i18n.t('shared:courses_limit_exceeded'),
          title: i18n.t('shared:congrats_you_on_roll'),
        };
      }
    }

    if (!(await alert.confirm())) {
      return COMMAND.CANCEL;
    }

    const startedCourse = (await courseStore.start(entity, {
      ...params,
      expand: courseExpandCommon,
    })) as CourseExpandedCommon;

    requestReviewIfNeeded();

    courseStore.localUpdate(entity, startedCourse);

    logger.event('squad_start_success');

    return startedCourse;
  } catch (e) {
    if (e.title) {
      alert.show({
        buttons: e.buttons,
        message: e.message,
        title: e.title,
      });

      return;
    }

    throw e;
  }
}

/**
 * It's a hack to request a review after a course is started,
 * since a course can be started from different places in the app.
 */
const requestReviewIfNeeded = async () => {
  if (platform.isNative) {
    try {
      const response: ApiCountResponse =
        await apiRequest<CoreCoachCoursesCountParams>({
          method: 'coach.courses.count',
          params: {
            query: [
              ['coach_ids', 'in', [currentUserStore.user!._id]],
              ['status', '==', 'ongoing'],
            ],
          },
        });

      emitter.emit(REQUEST_REVIEW_AFTER_COURSE_START_EVENT, response._count);
    } catch (error) {
      logger.error(error);
    }
  }
};
